import React from 'react';
import { BoltIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { cn } from '@/lib/utils';
import { DatePicker } from '../ui/date-picker';
import { Button } from '../ui/button';
import { Tip } from '../ui/tip';
import { sumarDias, proximoDiaSemana } from '../../utils/tareasFechas';
import { ModoCuando, SlotInfo, slotsVigentes, fmtDuracion, sumarMinutos, horaHM, etiquetaDia } from '../../utils/turnoNuevo';

interface Props {
  modo: ModoCuando;
  onModo: (m: ModoCuando) => void;
  fecha: string;
  onFecha: (ymd: string) => void;
  slot: string;
  onSlot: (hhmm: string) => void;
  slots: SlotInfo[];
  cargando: boolean;
  duracion: number;
  hoy: string;
  ahora: Date;
}

const OPCIONES: Array<{ key: ModoCuando; label: string; icono?: React.ReactNode }> = [
  { key: 'ahora', label: 'Ahora', icono: <BoltIcon className="h-4 w-4" /> },
  { key: 'hoy', label: 'Hoy a las…' },
  { key: 'otro', label: 'Otro día' }
];

/**
 * Bloque "Cuándo": Ahora (entra al taller ya, nace en proceso), Hoy a una
 * hora, u Otro día. Los horarios se piden con la duración real de lo cargado
 * y muestran el semáforo del taller sin bloquear nada.
 */
const CuandoTurno: React.FC<Props> = ({ modo, onModo, fecha, onFecha, slot, onSlot, slots, cargando, duracion, hoy, ahora }) => {
  const dia = modo === 'hoy' ? hoy : modo === 'otro' ? fecha : '';
  const visibles = dia ? slotsVigentes(slots, dia, ahora) : [];
  const lunes = proximoDiaSemana(hoy, 1);

  const renderSlots = () => {
    if (!dia) return null;
    if (cargando) return <p className="mt-3 text-sm text-muted-foreground">Buscando horarios…</p>;
    if (visibles.length === 0) {
      return (
        <p className="mt-3 text-sm text-muted-foreground">
          {modo === 'hoy' ? 'No quedan horarios para hoy: usá "Ahora" u "Otro día".' : 'No hay horarios cargados para esa fecha.'}
        </p>
      );
    }
    return (
      <>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {visibles.map((s) => {
            const elegido = s.time === slot;
            const chip = (
              <button
                type="button"
                onClick={() => onSlot(s.time)}
                aria-pressed={elegido}
                className={cn(
                  'inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-sm tabular-nums transition-colors',
                  elegido
                    ? 'border-primary bg-primary text-primary-foreground'
                    : s.nivel === 'lleno'
                      ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-800 dark:bg-red-950/60 dark:text-red-300'
                      : s.nivel === 'sobreturno'
                        ? 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-300'
                        : 'border-border bg-background hover:bg-accent'
                )}
              >
                {s.nivel !== 'libre' && !elegido && <ExclamationTriangleIcon className="h-3.5 w-3.5" />}
                {s.time}
                {s.nivel !== 'libre' && <span className="text-[11px] opacity-80">{s.ocupados}/{s.capacidad}</span>}
              </button>
            );
            return s.nivel === 'libre'
              ? <React.Fragment key={s.time}>{chip}</React.Fragment>
              : <Tip key={s.time} label={`${s.nivel === 'lleno' ? 'Lleno' : 'Sobreturno'}: ${s.ocupados}/${s.capacidad} ocupado. Se puede agendar igual`}>{chip}</Tip>;
          })}
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-emerald-500 align-[-1px]" />Libre</span>
          <span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-amber-500 align-[-1px]" />Sobreturno</span>
          <span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-red-500 align-[-1px]" />Lleno · todos se pueden elegir igual</span>
          {slot && <span>Con {fmtDuracion(duracion)} de trabajo, {slot} termina ~{sumarMinutos(slot, duracion)}</span>}
        </div>
      </>
    );
  };

  return (
    <div>
      <div className="inline-flex rounded-lg border border-border bg-muted/40 p-0.5" role="group" aria-label="Cuándo">
        {OPCIONES.map((o) => (
          <button
            key={o.key}
            type="button"
            aria-pressed={modo === o.key}
            onClick={() => onModo(o.key)}
            className={cn(
              'inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-sm font-medium transition-colors',
              modo === o.key ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {o.icono}{o.label}
          </button>
        ))}
      </div>

      {modo === '' && (
        <p className="mt-3 text-sm text-muted-foreground">Elegí si el vehículo entra ahora, viene hoy a una hora o queda para otro día.</p>
      )}

      {modo === 'ahora' && (
        <div className="mt-3 flex items-start gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2.5">
          <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
          <div className="text-sm">
            <div className="font-medium">Entra al taller ahora, {horaHM(ahora)}</div>
            <div className="text-muted-foreground">Nace "en proceso" y aparece en Taller al instante. Con {fmtDuracion(duracion)} de trabajo estaría listo ~{sumarMinutos(horaHM(ahora), duracion)}.</div>
          </div>
        </div>
      )}

      {modo === 'hoy' && renderSlots()}

      {modo === 'otro' && (
        <div className="mt-3">
          <div className="flex flex-wrap items-center gap-2">
            <DatePicker value={fecha} onChange={onFecha} minDate={hoy} placeholder="Elegir fecha" className="h-9 w-[180px]" />
            <div className="inline-flex gap-1.5">
              <Button type="button" variant={fecha === sumarDias(hoy, 1) ? 'default' : 'outline'} className="h-9" onClick={() => onFecha(sumarDias(hoy, 1))}>Mañana</Button>
              <Button type="button" variant={fecha === lunes ? 'default' : 'outline'} className="h-9" onClick={() => onFecha(lunes)}>Lunes</Button>
            </div>
            {fecha && <span className="text-sm text-muted-foreground">{etiquetaDia(fecha, hoy)}</span>}
          </div>
          {renderSlots()}
        </div>
      )}
    </div>
  );
};

export default CuandoTurno;
